import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { CreateAccountProfile, CreateAccountText, OtpOutcome } from "./types";
import { LanguageToggle } from "./LanguageToggle";

export interface CreateAccountStepProps {
  /** The verified phone number, shown in the badge above the heading. */
  phoneNumber: string;
  onSubmit: (profile: CreateAccountProfile) => Promise<OtpOutcome>;
  isSubmitting: boolean;
  /** Override any of the step's strings; missing ones use the defaults. */
  text?: Partial<CreateAccountText> | undefined;
  /** Show the optional email field. Defaults to true. */
  showEmail?: boolean | undefined;
  /** Show the "email me a receipt" checkbox. Defaults to false. */
  showReceiptOptIn?: boolean | undefined;
  /** Show the language toggle. Defaults to true. */
  showLanguageToggle?: boolean | undefined;
  /** When provided, renders a close (X) button. */
  onClose?: (() => void) | undefined;
  /** Render flat content (no card chrome) when embedded in a modal dialog. */
  bare?: boolean | undefined;
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** "+85291234567" → "+852 9123 4567"; other numbers are shown as they are. */
export const formatPhoneForDisplay = (phone: string): string => {
  const hk = /^\+852(\d{4})(\d{4})$/.exec(phone);
  return hk ? `+852 ${hk[1]} ${hk[2]}` : phone;
};

type FieldError = "firstName" | "lastName" | "email";

interface FilledFieldProps {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string | undefined;
  type?: string;
  autoComplete?: string;
  invalid?: boolean;
}

// A filled field with its label inside, as in the hailing mock-up: grey at rest,
// a blue border while focused, a danger border when invalid.
const FilledField: React.FC<FilledFieldProps> = ({
  id,
  label,
  value,
  onChange,
  placeholder,
  type = "text",
  autoComplete,
  invalid = false,
}) => (
  <div
    className={`flex min-w-0 flex-1 flex-col gap-0.5 rounded-2xl border-2 bg-[#F2F3F3] px-4 py-2.5 ${
      invalid
        ? "border-danger-ink"
        : "border-transparent focus-within:border-brand-blue focus-within:bg-white"
    }`}
  >
    <label
      htmlFor={id}
      className={`font-lexend text-[12px]/[16px] font-normal ${
        invalid ? "text-danger-ink" : "text-ink-60"
      }`}
    >
      {label}
    </label>
    <input
      id={id}
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      autoComplete={autoComplete}
      aria-invalid={invalid || undefined}
      className="w-full bg-transparent font-lexend text-[16px]/[20px] font-normal text-ink-100 outline-none placeholder:text-ink-40"
    />
  </div>
);

export const CreateAccountStep: React.FC<CreateAccountStepProps> = ({
  phoneNumber,
  onSubmit,
  isSubmitting,
  text,
  showEmail = true,
  showReceiptOptIn = false,
  showLanguageToggle = true,
  onClose,
  bare = false,
}) => {
  const { t } = useTranslation("sharedLogin");
  const displayPhone = formatPhoneForDisplay(phoneNumber);
  const copy: CreateAccountText = {
    verifiedLabel: t("createAccount.verifiedLabel", { phone: displayPhone }),
    title: t("createAccount.title"),
    // No description by default; a host passes its own (e.g. web hailing).
    description: "",
    firstNameLabel: t("createAccount.firstNameLabel"),
    lastNameLabel: t("createAccount.lastNameLabel"),
    emailLabel: t("createAccount.emailLabel"),
    // No placeholder by default; a host can pass one.
    emailPlaceholder: "",
    receiptOptInLabel: t("createAccount.receiptOptInLabel"),
    submitLabel: t("createAccount.submitLabel"),
    submittingLabel: t("createAccount.submittingLabel"),
    ...text,
  };
  // A host-supplied verified label may carry its own {{phone}} placeholder.
  const verifiedLabel = copy.verifiedLabel.replace("{{phone}}", displayPhone);

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [emailReceipts, setEmailReceipts] = useState(true);
  const [errors, setErrors] = useState<FieldError[]>([]);
  const [submitFailed, setSubmitFailed] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;
    const trimmedEmail = email.trim();
    const nextErrors: FieldError[] = [];
    if (!firstName.trim()) nextErrors.push("firstName");
    if (!lastName.trim()) nextErrors.push("lastName");
    if (showEmail && trimmedEmail && !EMAIL_PATTERN.test(trimmedEmail)) {
      nextErrors.push("email");
    }
    setErrors(nextErrors);
    if (nextErrors.length > 0) return;

    setSubmitFailed(false);
    const result = await onSubmit({
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      ...(showEmail && trimmedEmail ? { email: trimmedEmail } : {}),
      emailReceipts: showReceiptOptIn && emailReceipts,
    });
    if (!result.success) setSubmitFailed(true);
  };

  return (
    <div
      className={
        bare
          ? "relative flex flex-col gap-4 md:gap-6"
          : "relative flex flex-col gap-4 md:gap-6 rounded-2xl border-2 border-ink-12 bg-card p-5 md:p-8"
      }
    >
      {(onClose || showLanguageToggle) && (
        <div className="flex items-center gap-3 self-end">
          {showLanguageToggle && <LanguageToggle />}
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              aria-label={t("common.close")}
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#F2F3F3] text-ink-60"
            >
              <span className="font-material-symbols text-[20px] leading-none">
                close
              </span>
            </button>
          )}
        </div>
      )}

      <div className="flex flex-col gap-2">
        <p className="flex items-center gap-1.5 font-lexend text-[13px]/[16px] font-semibold text-success-ink">
          <span
            aria-hidden="true"
            className="font-material-symbols text-[18px] leading-none [font-variation-settings:'FILL'_1]"
          >
            verified
          </span>
          {verifiedLabel}
        </p>
        <h1 className="font-lexend text-[24px]/[28px] font-bold tracking-[-0.8px] text-ink-100">
          {copy.title}
        </h1>
        {copy.description && (
          <p className="font-lexend text-[14px]/[18px] font-normal text-ink-60">
            {copy.description}
          </p>
        )}
      </div>

      <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-3">
        <div className="flex gap-3">
          <FilledField
            id="shared-login-first-name"
            label={copy.firstNameLabel}
            value={firstName}
            onChange={setFirstName}
            autoComplete="given-name"
            invalid={errors.includes("firstName")}
          />
          <FilledField
            id="shared-login-last-name"
            label={copy.lastNameLabel}
            value={lastName}
            onChange={setLastName}
            autoComplete="family-name"
            invalid={errors.includes("lastName")}
          />
        </div>

        {/* Field problems show as a red border only, with no message text. */}
        {showEmail && (
          <FilledField
            id="shared-login-email"
            label={copy.emailLabel}
            value={email}
            onChange={setEmail}
            placeholder={copy.emailPlaceholder || undefined}
            type="email"
            autoComplete="email"
            invalid={errors.includes("email")}
          />
        )}

        {showReceiptOptIn && (
          <label className="flex items-center gap-2.5 py-1">
            <input
              type="checkbox"
              checked={emailReceipts}
              onChange={(e) => setEmailReceipts(e.target.checked)}
              className="h-5 w-5 shrink-0 rounded accent-brand-blue"
            />
            <span className="font-lexend text-[14px]/[18px] font-normal text-ink-100">
              {copy.receiptOptInLabel}
            </span>
          </label>
        )}

        {submitFailed && (
          <p className="font-lexend text-[14px]/[18px] font-normal text-danger-ink">
            {t("createAccount.errSubmitFailed")}
          </p>
        )}

        <button
          type="submit"
          disabled={isSubmitting}
          className={`mt-1 flex h-14 w-full items-center justify-center rounded-2xl font-lexend text-[16px]/[20px] font-bold text-white ${
            isSubmitting ? "cursor-not-allowed bg-gray-300" : "bg-brand-blue"
          }`}
        >
          {isSubmitting ? copy.submittingLabel : copy.submitLabel}
        </button>
      </form>
    </div>
  );
};
